<script setup lang="ts">
// A command menu at (x, y): a floating panel of 44 px items, with optional headers,
// dividers and a danger style.
import type { ContextMenuItem } from '../types.ts';

withDefaults(defineProps<{ x?: number; y?: number; header?: string; items?: readonly ContextMenuItem[]; width?: number }>(), {
  x: 0,
  y: 0,
  header: '',
  items: () => [],
  width: 240,
});
const emit = defineEmits<{ close: [] }>();

const run = (item: ContextMenuItem): void => {
  if (!('label' in item) || item.disabled) return;
  item.onClick?.();
  emit('close');
};
</script>

<template>
  <div class="itc-ctx" role="menu" :style="{ left: `${x}px`, top: `${y}px`, width: `${width}px` }" @click.stop>
    <div v-if="header" class="itc-ctx__header">{{ header }}</div>
    <template v-for="(item, i) in items" :key="i">
      <div v-if="'divider' in item" class="itc-ctx__divider" role="separator"></div>
      <div v-else-if="'header' in item" class="itc-ctx__header">{{ item.header }}</div>
      <div
        v-else
        class="itc-ctx__item"
        role="menuitem"
        tabindex="0"
        :aria-disabled="item.disabled || undefined"
        :class="{ 'is-danger': item.danger, 'is-disabled': item.disabled }"
        @click="run(item)"
        @keydown.enter.prevent="run(item)"
      >
        <span>{{ item.label }}</span>
        <span v-if="item.hasChildren" class="itc-ctx__chevron" aria-hidden="true">›</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.itc-ctx {
  position: fixed;
  z-index: 10000;
  overflow: hidden;
  padding: 4px;
  background: var(--itc-panel);
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-control);
  box-shadow: var(--itc-shadow-pop);
}
.itc-ctx__header {
  padding: 8px 12px 4px;
  color: var(--itc-muted);
  font-family: var(--itc-font-display);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.itc-ctx__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: var(--itc-tap);
  padding: 0 12px;
  border-radius: var(--itc-r-input);
  color: var(--itc-fg);
  font-weight: 700;
  cursor: pointer;
  user-select: none;
}
.itc-ctx__item:hover,
.itc-ctx__item:focus-visible {
  background: var(--itc-panel-2);
}
.itc-ctx__item.is-danger {
  color: var(--itc-crit);
}
.itc-ctx__item.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.itc-ctx__item.is-disabled:hover {
  background: transparent;
}
.itc-ctx__divider {
  height: 1px;
  margin: 4px 0;
  background: var(--itc-line);
}
.itc-ctx__chevron {
  color: var(--itc-muted);
  font-size: 18px;
}
</style>
